import cn from 'clsx'
import { ComponentProps, FC } from 'react'

import VideoPlayer from '@/components/VideoPlayer'
import { Icon, Typography } from '@/components/ui'
import Spinner from '@/components/ui/spinner/Spinner'

import { ITask } from '@/types/task.interface'

import HighlightControls from './HighlightControls'
import HighlightFooterControls from './HighlightFooterControls'
import HighlightList from './HighlightList'

const statuses = {
	PENDING: 'The task has been added to the queue',
	STARTED: 'Highlight is being created',
	RETRY: 'Highlight is being created'
}

interface ILiveSidebar {
	task: ITask | undefined
	isCollapsed: boolean
	isLoading: boolean
	isUploadHighlightLoading: boolean
	isJobLoading: boolean
	isShareLinkCreating: boolean
	jobStatus: string | null
	onBack: () => void
	onPlayerReady: ComponentProps<typeof VideoPlayer>['onPlayerReady']
	controlsProps: Omit<
		ComponentProps<typeof HighlightControls>,
		'taskId' | 'isLoading' | 'isShareLinkCreating'
	>
	listProps: Omit<
		ComponentProps<typeof HighlightList>,
		'isLoading' | 'isJobLoading'
	>
	footerProps: ComponentProps<typeof HighlightFooterControls>
}

const LiveSidebar: FC<ILiveSidebar> = ({
	task,
	isCollapsed,
	isLoading,
	isUploadHighlightLoading,
	isJobLoading,
	isShareLinkCreating,
	jobStatus,
	onBack,
	onPlayerReady,
	controlsProps,
	listProps,
	footerProps
}) => {
	const isProcessing =
		isUploadHighlightLoading || isJobLoading || isShareLinkCreating

	return (
		<aside
			className={cn(
				'border-r border-black/10 flex flex-col justify-between overflow-hidden relative transition-all',
				isCollapsed ? 'w-[32%]' : 'w-[25%]'
			)}
		>
			{isProcessing && (
				<div className='absolute z-20 top-[45%] left-1/2 -translate-x-1/2 h-[150px] flex flex-col items-center px-5 py-7 bg-blue-50'>
					<div className='relative mx-auto'>
						<Spinner className='!absolute !w-10 !h-10 !top-0 !left-0 !border-t-blue-500 !border-l-blue-500' />
					</div>
					<div className='text-blue-500 mt-10 text-center'>
						{jobStatus ? statuses[jobStatus as keyof typeof statuses] : null}
					</div>
					<p className='text-blue-500 text-center'>Please wait for a while</p>
				</div>
			)}

			<button onClick={onBack} className='absolute z-50 top-2 left-2'>
				<Icon size={24} icon='arrow-back' color='#fff' />
			</button>

			<VideoPlayer
				src={task?.video?.src}
				type={task?.video?.type}
				isLoading={isLoading}
				onPlayerReady={onPlayerReady}
			/>

			<div className='p-2 text-center bg-gray-200'>
				<Typography variant='text-semibold'>
					Audio tracks amount: <span>{task?.audio_tracks}</span>
				</Typography>
			</div>

			{!isLoading && (
				<>
					<HighlightControls
						{...controlsProps}
						taskId={task.id}
						isLoading={isProcessing}
						isShareLinkCreating={isShareLinkCreating}
					/>

					<HighlightList {...listProps} />
				</>
			)}

			<HighlightFooterControls {...footerProps} />
		</aside>
	)
}

export default LiveSidebar
